(() => {
  'use strict';
  if (customElements.get('soulmade-combination')) return;

  class SoulmadeCombination extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      // Deferred scripts upgrade existing markup; editor insertions may connect before children exist.
      if (!this.querySelector('[data-catalog]')) {
        this.readyObserver = new MutationObserver(() => {
          if (this.querySelector('[data-catalog]')) {
            this.readyObserver.disconnect();
            this.connectedCallback();
          }
        });
        this.readyObserver.observe(this, { childList: true, subtree: true });
        return;
      }
      this.initialized = true;
      this.abort = new AbortController();
      this.products = new Map();
      this.lines = new Map();
      this.size = 'Mini';
      this.editing = null;
      this.choice = null;
      this.busy = false;
      this.uncertain = false;
      this.compositionStart = 0;
      this.compositionSignature = '';
      this.revealId = null;
      this.money = new Intl.NumberFormat(this.dataset.locale || 'es-CO', {
        style: 'currency', currency: this.dataset.currency || 'COP', maximumFractionDigits: 2,
      });
      this.querySelectorAll('[data-reference]').forEach((node) => {
        const d = node.dataset;
        if (this.products.has(d.id)) return;
        const product = { id: d.id, size: d.size, color: d.color, price: Number(d.price),
          available: d.available === 'true', min: Number(d.min) || 1, step: Number(d.step) || 1,
          max: d.max ? Number(d.max) : null, image: node.querySelector('[data-source-image] img') || node.querySelector('img'),
          comparisonImage: node.querySelector('[data-comparison-image] img'),
          editorialImage: node.querySelector('[data-editorial-image] img'),
          swatch: /^#[0-9a-f]{6}$/i.test(d.swatch || '') ? d.swatch : null, block: d.blockId };
        if (!/^\d+$/.test(product.id) || !['Mini', 'Midi', 'Maxi'].includes(product.size) ||
          !product.color || !product.image || !Number.isSafeInteger(product.price) || product.price < 0) return;
        this.products.set(product.id, product);
      });
      this.querySelector('[data-sizes]').disabled = false;
      const notes = this.querySelector('[data-config-notes]');
      if (notes) {
        this.querySelectorAll('[data-config-error]').forEach((node) => notes.append(node.cloneNode(true)));
        const count = document.createElement('p');
        count.textContent = `${this.products.size} referencias válidas. ${[...this.products.values()].filter((p) => !p.swatch).length} pendientes de muestra de color. Precios y disponibilidad se obtienen de Shopify.`;
        notes.append(count);
      }
      this.addEventListener('click', (event) => this.onClick(event), { signal: this.abort.signal });
      document.addEventListener('shopify:block:select', (event) => {
        const product = [...this.products.values()].find((p) => p.block === event.detail?.blockId);
        if (!product || this.busy || this.uncertain) return;
        this.editing = null;
        this.size = product.size;
        this.choice = product.id;
        this.render();
      }, { signal: this.abort.signal });
      this.render();
      if (!this.products.size) this.message('Estamos preparando los colores de esta experiencia. Vuelve pronto.');
    }

    disconnectedCallback() {
      this.readyObserver?.disconnect();
      this.abort?.abort();
      this.initialized = false;
    }

    format(amount) { return this.money.format(amount / 100); }
    message(text) { this.querySelector('[data-message]').textContent = text; }
    validQuantity(product, quantity) {
      return Number.isSafeInteger(quantity) && quantity >= product.min &&
        quantity % product.step === 0 && (product.max === null || quantity <= product.max);
    }
    setQuantity(id, quantity) {
      const product = this.products.get(id);
      if (!product || !product.available || !this.validQuantity(product, quantity)) {
        this.message('Esa cantidad no está permitida para esta referencia. Ajusta la selección.');
        return false;
      }
      this.lines.set(id, quantity);
      return true;
    }
    changeColor(from, to) {
      if (from === to) return true;
      const source = this.products.get(from);
      const destination = this.products.get(to);
      if (!source || !destination || source.size !== destination.size || !this.lines.has(from)) return false;
      const total = this.lines.get(from) + (this.lines.get(to) || 0);
      if (!this.setQuantity(to, total)) return false;
      this.lines.delete(from);
      this.editing = to;
      return true;
    }

    onClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button) || button.disabled) return;
      if (button.dataset.action === 'toggle-selection') {
        this.toggleSelection();
        return;
      }
      if (this.busy || this.uncertain) return;
      const { action, id, size, colorId } = button.dataset;
      if (action !== 'submit') this.message('');
      if (size) { this.size = size; this.editing = null; this.choice = null; }
      if (colorId) {
        this.choice = colorId;
      }
      if (action === 'edit') {
        this.editing = id;
        this.size = this.products.get(id).size;
        this.choice = id;
        this.revealId = id;
      }
      if (action === 'cancel') {
        this.choice = this.editing;
        this.editing = null;
        this.message('Cambio cancelado. Tu selección no cambió.');
      }
      if (action === 'save-color' && this.editing) {
        if (!this.changeColor(this.editing, this.choice)) return;
        this.revealId = this.choice;
        this.editing = null;
        this.message('Color guardado en tu combinación.');
      }
      if (action === 'more') this.compositionStart += 6;
      if (action === 'add' && this.choice && !this.editing) {
        const p = this.products.get(this.choice);
        if (!this.setQuantity(p.id, (this.lines.get(p.id) || 0) + (this.lines.has(p.id) ? p.step : p.min))) return;
        this.revealId = p.id;
        this.message(`${p.size} ${p.color} agregada a tu combinación.`);
      }
      if (action === 'plus' || action === 'minus') {
        const p = this.products.get(id);
        if (!this.setQuantity(id, this.lines.get(id) + (action === 'plus' ? p.step : -p.step))) return;
      }
      if (action === 'remove') {
        this.lines.delete(id);
        if (this.editing === id) this.editing = null;
        this.message('Referencia eliminada de tu combinación.');
      }
      if (action === 'submit') {
        if (this.editing) { this.message('Guarda o cancela el cambio de color antes de continuar.'); return; }
        if (window.matchMedia('(max-width: 749px)').matches && !this.querySelector('.sm-selection').classList.contains('details-open')) {
          this.toggleSelection(true);
          this.message('Revisa tu selección y pulsa de nuevo Agregar combinación al carrito para continuar.');
          this.querySelector('.sm-selection').scrollIntoView({ block: 'start', behavior: 'auto' });
          return;
        }
        this.submit(); return;
      }
      this.render();
      // Rebuilding controls must preserve keyboard focus, including a merged color edit.
      if (action === 'edit') {
        const target = [...this.querySelectorAll('[data-color-id]')].find((n) => n.dataset.colorId === this.choice);
        target?.focus({ preventScroll: true });
        this.querySelector('.sm-controls').scrollIntoView({ block: 'nearest', behavior: 'auto' });
      } else if (action === 'cancel' || action === 'save-color') {
        this.querySelector('[data-action="add"]').focus({ preventScroll: true });
      } else if (colorId) {
        [...this.querySelectorAll('[data-color-id]')].find((n) => n.dataset.colorId === colorId)?.focus({ preventScroll: true });
      } else if (id) {
        const target = [...this.querySelectorAll('[data-action]')].find((n) => n.dataset.id === id && n.dataset.action === action);
        (target || this.querySelector('[data-action="add"]')).focus({ preventScroll: true });
      }
    }

    toggleSelection(force) {
      const section = this.querySelector('.sm-selection');
      const open = typeof force === 'boolean' ? force : !section.classList.contains('details-open');
      section.classList.toggle('details-open', open);
      const toggle = this.querySelector('[data-action="toggle-selection"]');
      toggle.setAttribute('aria-expanded', String(open));
      toggle.textContent = open ? 'Ocultar detalle −' : 'Ver/editar mi selección +';
    }

    button(text, action, id) {
      const node = document.createElement('button');
      node.type = 'button'; node.textContent = text;
      if (action) node.dataset.action = action;
      if (id) node.dataset.id = id;
      node.disabled = this.busy || this.uncertain;
      return node;
    }
    image(product, small = false) {
      const image = (small ? product.comparisonImage || product.image : product.image).cloneNode(true);
      image.removeAttribute('id'); image.alt = `${product.size} · ${product.color}`;
      image.sizes = small ? '90px' : '(min-width: 990px) 280px, 50vw';
      return image;
    }

    render() {
      const locked = this.busy || this.uncertain;
      const available = [...this.products.values()].filter((p) => p.size === this.size);
      if (!available.some((p) => p.id === this.choice)) this.choice = available.find((p) => p.available)?.id || null;
      this.querySelectorAll('button[data-size]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.size === this.size));
        button.disabled = locked || ![...this.products.values()].some((p) => p.size === button.dataset.size);
      });
      const colors = this.querySelector('[data-colors]');
      colors.replaceChildren();
      colors.dataset.presentation = 'swatches';
      available.forEach((p) => {
        const button = this.button(''); button.className = 'sm-color'; button.dataset.colorId = p.id;
        button.setAttribute('aria-pressed', String(this.choice === p.id));
        button.disabled = locked || !p.available;
        const sample = document.createElement('span'); sample.className = 'sm-color-swatch';
        sample.setAttribute('aria-hidden', 'true');
        if (p.swatch) sample.style.backgroundColor = p.swatch;
        else { sample.classList.add('sm-swatch-missing'); sample.textContent = '?'; }
        button.append(sample);
        button.dataset.soldOut = String(!p.available);
        if (!p.swatch) button.setAttribute('aria-label', `${p.color}: muestra de color pendiente${p.available ? '' : ', agotado'}`);
        const label = document.createElement('span'); label.textContent = p.color; button.append(label);
        if (!p.available) { const note = document.createElement('small'); note.textContent = 'Agotado'; button.append(note); }
        colors.append(button);
      });
      const chosen = this.products.get(this.choice);
      this.querySelector('[data-choice]').textContent = chosen ? `${chosen.size} · ${chosen.color} — ${this.format(chosen.price)}` : 'No hay colores disponibles para este tamaño.';
      this.querySelector('[data-color-heading]').textContent = this.editing ? 'Cambia el color de esta caja' : '2. Elige el color';
      this.querySelector('[data-action="cancel"]').hidden = !this.editing;
      this.querySelector('[data-action="cancel"]').disabled = locked;
      const save = this.querySelector('[data-action="save-color"]');
      save.hidden = !this.editing;
      save.disabled = locked || !chosen?.available || this.choice === this.editing;
      const editStatus = this.querySelector('[data-edit-status]');
      const edited = this.products.get(this.editing);
      editStatus.hidden = !edited;
      editStatus.textContent = edited ? `Editando ${edited.size} · ${edited.color}. El cambio se aplicará al guardar.` : '';
      this.querySelector('.sm-controls').classList.toggle('is-editing', !!edited);
      const add = this.querySelector('[data-action="add"]');
      add.hidden = !!this.editing; add.disabled = locked || !chosen?.available;
      const composition = this.querySelector('[data-composition]');
      if (!this.emptyState) this.emptyState = composition.firstElementChild.cloneNode(true);
      composition.replaceChildren();
      const selection = this.querySelector('[data-selection]');
      selection.replaceChildren();
      const entries = [...this.lines];
      // Interleave sizes so the limited view remains balanced without changing selection order.
      const buckets = ['Mini', 'Midi', 'Maxi'].map((size) => entries.filter(([id]) => this.products.get(id).size === size));
      const ordered = [];
      for (let i = 0; i < Math.max(0, ...buckets.map((bucket) => bucket.length)); i++) {
        buckets.forEach((bucket) => { if (bucket[i]) ordered.push(bucket[i]); });
      }
      const signature = ordered.map(([id]) => id).join(',');
      if (signature !== this.compositionSignature) { this.compositionStart = 0; this.compositionSignature = signature; }
      this.compositionStart = ordered.length ? this.compositionStart % ordered.length : 0;
      if (this.revealId) {
        const index = ordered.findIndex(([id]) => id === this.revealId);
        if (index >= 0 && (index - this.compositionStart + ordered.length) % ordered.length >= 6) this.compositionStart = index;
        this.revealId = null;
      }
      const visibleEntries = ordered.length > 6
        ? Array.from({ length: 6 }, (_, i) => ordered[(this.compositionStart + i) % ordered.length]) : ordered;
      const nav = this.querySelector('[data-composition-nav]');
      nav.hidden = ordered.length <= 6;
      this.querySelector('[data-visible-count]').textContent = `6 de ${ordered.length} referencias`;
      this.querySelector('[data-action="more"]').disabled = locked;
      composition.dataset.groups = String(new Set(visibleEntries.map(([id]) => this.products.get(id).size)).size);
      ['Mini', 'Midi', 'Maxi'].forEach((size) => {
        const groupEntries = visibleEntries.filter(([id]) => this.products.get(id).size === size);
        if (!groupEntries.length) return;
        const group = document.createElement('section'); group.className = 'sm-photo-group'; group.dataset.size = size;
        group.setAttribute('aria-label', `Composición ${size}`);
        const gallery = document.createElement('div'); gallery.className = 'sm-photo-gallery';
        gallery.dataset.dense = String(groupEntries.length > 2);
        groupEntries.forEach(([id, quantity]) => {
          const p = this.products.get(id);
          const edit = this.button('', 'edit', id); edit.className = 'sm-photo-choice';
          edit.setAttribute('aria-label', `Cambiar color: ${p.size} ${p.color}, cantidad ${quantity}`);
          edit.setAttribute('aria-pressed', String(this.editing === id));
          const visual = document.createElement('span'); visual.className = 'sm-photo-visual';
          const fallback = () => {
            visual.replaceChildren();
            const missing = document.createElement('span'); missing.className = 'sm-photo-missing';
            const patch = document.createElement('span'); patch.className = 'sm-fallback-swatch';
            if (p.swatch) patch.style.backgroundColor = p.swatch;
            patch.setAttribute('aria-hidden', 'true');
            const note = document.createElement('span'); note.textContent = 'Muestra de color · Foto pendiente';
            missing.append(patch, note); visual.append(missing);
          };
          if (p.editorialImage) {
            const img = p.editorialImage.cloneNode(true); img.removeAttribute('id');
            img.alt = `${p.size} · ${p.color}`; img.sizes = '(min-width: 990px) 260px, 45vw';
            img.addEventListener('error', fallback, { once: true }); visual.append(img);
          } else fallback();
          const label = document.createElement('span'); label.className = 'sm-photo-label';
          label.append(document.createTextNode(p.color));
          edit.append(visual, label); gallery.append(edit);
        });
        const heading = document.createElement('h3'); heading.textContent = size;
        group.append(heading, gallery); composition.append(group);
      });
      let count = 0; let subtotal = 0;
      this.lines.forEach((quantity, id) => {
        const p = this.products.get(id); count += quantity; subtotal += p.price * quantity;
        const line = document.createElement('div'); line.className = 'sm-selection-row';
        line.dataset.editing = String(this.editing === id);
        const info = document.createElement('div'); info.className = 'sm-reference-name';
        const name = document.createElement('strong'); name.textContent = `${p.size} · ${p.color}`;
        const unit = document.createElement('small'); unit.textContent = `${this.format(p.price)} / caja`;
        info.append(name, unit);
        const controls = document.createElement('div'); controls.className = 'sm-quantity';
        const minus = this.button('−', 'minus', id); minus.setAttribute('aria-label', `Reducir cantidad de ${p.size} ${p.color}`);
        minus.disabled = locked || !this.validQuantity(p, quantity - p.step);
        const output = document.createElement('output'); output.textContent = quantity; output.setAttribute('aria-label', `Cantidad de ${p.size} ${p.color}`);
        const plus = this.button('+', 'plus', id); plus.setAttribute('aria-label', `Aumentar cantidad de ${p.size} ${p.color}`);
        plus.disabled = locked || !this.validQuantity(p, quantity + p.step);
        controls.append(minus, output, plus);
        const price = document.createElement('strong'); price.className = 'sm-line-price'; price.textContent = this.format(p.price * quantity);
        const actions = document.createElement('div'); actions.className = 'sm-line-actions';
        const edit = this.button('Cambiar color', 'edit', id); edit.className = 'sm-text-button';
        edit.setAttribute('aria-label', `Cambiar color: ${p.size} ${p.color}`);
        const remove = this.button('Eliminar', 'remove', id); remove.className = 'sm-text-button';
        remove.setAttribute('aria-label', `Eliminar ${p.size} ${p.color}`);
        actions.append(edit, remove); line.append(info, controls, price, actions); selection.append(line);
      });
      if (!count) { const empty = document.createElement('p'); empty.className = 'sm-selection-empty'; empty.textContent = 'Las cajas que elijas y sus cantidades aparecerán aquí.'; selection.append(empty); }
      if (!count) composition.append(this.emptyState.cloneNode(true));
      this.querySelector('[data-count]').textContent = `${count} ${count === 1 ? 'caja' : 'cajas'}`;
      this.querySelector('[data-total-count]').textContent = count ? `${count} ${count === 1 ? 'caja seleccionada' : 'cajas seleccionadas'}` : 'Tu selección';
      this.querySelector('[data-subtotal]').textContent = this.format(subtotal);
      const submit = this.querySelector('[data-action="submit"]');
      submit.disabled = locked || !count || !!this.editing;
      submit.textContent = this.busy ? 'Agregando…' : this.uncertain ? 'Revisa tu carrito para continuar' : 'Agregar combinación al carrito';
      this.setAttribute('aria-busy', String(this.busy));
    }

    async submit() {
      if (this.busy || this.uncertain || !this.lines.size) return;
      this.busy = true;
      this.render();
      this.message('Agregando tu combinación…');
      const link = this.querySelector('[data-cart-link]');
      link.hidden = true;
      const drawer = document.querySelector('cart-drawer');
      const sections = drawer?.getSectionsToRender?.() || [];
      const items = [...this.lines].map(([id, quantity]) => ({ id, quantity }));
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(this.dataset.addUrl, {
          method: 'POST', credentials: 'same-origin', signal: controller.signal,
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items, sections: sections.map((section) => section.id), sections_url: location.pathname }),
        });
        const data = await response.json();
        // Any rejection may coexist with a changed cart. Never automatically replay the POST.
        if (!response.ok || data.status || !Array.isArray(data.items)) {
          this.uncertain = true;
          const detail = typeof data.description === 'string' ? data.description : 'No se pudo confirmar la combinación completa.';
          this.message(`${detail} Revisa tu carrito antes de volver a crear la selección. No repetimos el envío.`);
          link.hidden = false;
          return;
        }
        this.lines.clear(); this.editing = null;
        this.message('Tu combinación se agregó al carrito.');
        link.hidden = false;
        // Render only after validating every fragment; a render failure must not retry add.js.
        if (drawer && sections.length && this.isConnected) {
          try {
            const parser = new DOMParser();
            const valid = sections.every((section) => {
              const html = data.sections?.[section.id];
              const selector = section.selector || '.shopify-section';
              return typeof html === 'string' && parser.parseFromString(html, 'text/html').querySelector(selector) &&
                (section.selector ? document.querySelector(section.selector) : document.getElementById(section.id));
            });
            if (!valid) throw new Error('Missing cart sections');
            const drawerDoc = parser.parseFromString(data.sections['cart-drawer'], 'text/html');
            if (!drawerDoc.querySelector('#CartDrawer-Overlay') || !drawerDoc.querySelector('.drawer__inner')) throw new Error('Incomplete drawer');
            drawer.setActiveElement(link);
            drawer.classList.remove('is-empty');
            drawer.renderContents(data);
          } catch {
            this.message('Tu combinación se agregó. Abre el carrito para revisarla.');
          }
        }
      } catch {
        this.uncertain = true;
        this.message('No pudimos confirmar la respuesta. Es posible que las cajas ya estén en tu carrito. Revísalo antes de volver a crear la selección; no repetimos el envío.');
        link.hidden = false;
      } finally {
        clearTimeout(timeout);
        this.busy = false;
        if (this.isConnected) this.render();
      }
    }
  }
  customElements.define('soulmade-combination', SoulmadeCombination);
})();
